// Used guide from here: https://docs.pmnd.rs/zustand/guides/nextjs
import { cloneDeep } from "lodash"
import { createStore } from "zustand/vanilla"
import { persist, createJSONStorage } from "zustand/middleware"

import { type IVariantSummary } from "~/hooks/useVariantTableData"

export type IForecasts = {
  warehouseId: string
  forecasts: Record<string, number[]>
  model: Record<string, string>
  specialSkuForecasts: Record<string, number[]>
}

export type IWeightMap = Map<string, { weight: number; variantId: string }>

export type SkuState = {
  warehouseId: string | undefined
  channel: string | undefined
  variants: IVariantSummary[]
  newVariantMap: Map<string, IVariantSummary>
}

export type SkuActions = {
  updateVariants: (variants: IVariantSummary[]) => void
  updateWarehouseId: (warehouseId: string | undefined) => void
  updateChannel: (channel: string | undefined) => void
}

export type SkuStore = SkuState & SkuActions

export const initSkuStore = (): SkuState => {
  return cloneDeep(defaultInitState)
}

export const defaultInitState: SkuState = {
  warehouseId: undefined,
  channel: undefined,
  variants: [],
  newVariantMap: new Map<string, IVariantSummary>(),
}

export const createSkuStore = (initState: SkuState) => {
  return createStore<SkuStore>()(
    persist(
      (set) => ({
        ...initState,
        updateVariants: (variants) =>
          set(() => ({
            variants,
            newVariantMap: new Map(variants.map((variant) => [variant.variantId, variant])),
          })),
        updateWarehouseId: (warehouseId) => set(() => ({ warehouseId: warehouseId ?? undefined })), // just to be extra sure that no null values are being suplied
        updateChannel: (channel) => set(() => ({ channel: channel ?? undefined })),
      }),
      {
        name: "sku-store",
        storage: createJSONStorage(() => sessionStorage),
        partialize: (state: SkuStore) => ({
          warehouseId: state.warehouseId,
          channel: state.channel,
        }),
      },
    ),
  )
}
